import { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, User, LogIn, AlertTriangle } from 'lucide-react';
import { login } from '../lib/auth';

export function Login({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (await login(username, password)) {
        onSuccess();
      } else {
        setError('Incorrect username or password.');
        setBusy(false);
      }
    } catch {
      setError('Unable to reach the server. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-text-primary relative flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-grid-pattern opacity-50" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(230, 0, 0, 0.10) 0%, transparent 60%)' }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-[400px]"
          style={{ background: 'radial-gradient(ellipse 60% 100% at 50% 100%, rgba(230, 0, 0, 0.05) 0%, transparent 70%)' }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-vokentra flex items-center justify-center mb-3 shadow-lg shadow-vokentra/30">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                fill="white"
              />
              <circle cx="12" cy="12" r="5" fill="white" />
            </svg>
          </div>
          <h1 className="text-xl font-bold tracking-tight">Vokentra</h1>
          <p className="text-sm text-text-muted">Voice Agentic Ai Platform</p>
        </div>

        {/* Card */}
        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/[0.08] bg-background/70 backdrop-blur-xl p-6 shadow-2xl"
        >
          <h2 className="text-base font-semibold mb-1">Sign in</h2>
          <p className="text-xs text-text-muted mb-5">Enter your credentials to access the console.</p>

          <label className="block text-xs text-text-muted mb-1.5">Username</label>
          <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 mb-4 focus-within:border-vokentra/50">
            <User className="w-4 h-4 text-text-muted" />
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="bg-transparent outline-none text-sm w-full text-text-primary placeholder:text-text-muted/60"
            />
          </div>

          <label className="block text-xs text-text-muted mb-1.5">Password</label>
          <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 mb-2 focus-within:border-vokentra/50">
            <Lock className="w-4 h-4 text-text-muted" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-transparent outline-none text-sm w-full text-text-primary placeholder:text-text-muted/60"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-danger text-xs mt-2 mb-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-vokentra text-white font-medium hover:bg-vokentra-dark disabled:opacity-60 transition-colors"
          >
            <LogIn className="w-4 h-4" />
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-[11px] text-text-muted mt-6">
          © {new Date().getFullYear()} BDST Vokentra · Voice Agentic Ai Platform
        </p>
      </motion.div>
    </div>
  );
}

export default Login;
